import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, StickyNote, Check, Loader2 } from 'lucide-react'
import { usePageStore } from '@/store/pageStore'
import { adminNoteApi, AdminNote } from '@/api/b2b'
import { clsx } from 'clsx'
import { toast } from 'sonner'

// 포스트잇 색상 팔레트
const NOTE_COLORS = [
  'bg-yellow-200',
  'bg-pink-200',
  'bg-blue-200',
  'bg-green-200',
  'bg-purple-200',
  'bg-orange-200',
]

export default function NotesPage() {
  const { t } = useTranslation('admin')
  const { setPageSubtitle } = usePageStore()
  const [notes, setNotes] = useState<AdminNote[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState<number | null>(null)

  // 페이지 부제목 설정
  useEffect(() => {
    setPageSubtitle(t('notes.title'))
    return () => setPageSubtitle(null)
  }, [setPageSubtitle])

  // 메모 목록 로드
  const loadNotes = useCallback(async () => {
    try {
      const response = await adminNoteApi.getAll()
      if (response.data.success && response.data.data) {
        setNotes(response.data.data)
      }
    } catch (error) {
      console.error('메모 로드 실패:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadNotes()
  }, [loadNotes])

  // 새 메모 추가
  const addNote = async () => {
    const randomColor = NOTE_COLORS[Math.floor(Math.random() * NOTE_COLORS.length)]
    try {
      const response = await adminNoteApi.create({ color: randomColor })
      if (response.data.success && response.data.data) {
        setNotes(prev => [response.data.data, ...prev])
      } else {
        console.error('메모 생성 실패: 응답이 성공하지 않았습니다', response.data)
        toast.error(t('notes.toast.createFailed'))
      }
    } catch (error) {
      console.error('메모 생성 실패:', error)
      toast.error(t('notes.toast.createError'))
    }
  }

  // 메모 삭제
  const deleteNote = async (id: number) => {
    try {
      await adminNoteApi.delete(id)
      setNotes(notes.filter(note => note.id !== id))
    } catch (error) {
      console.error('메모 삭제 실패:', error)
    }
  }

  // 메모 내용 수정 (디바운스 적용)
  const updateNote = async (id: number, content: string) => {
    // 로컬 상태 먼저 업데이트
    setNotes(notes.map(note =>
      note.id === id ? { ...note, content } : note
    ))
    
    // 디바운스된 API 호출
    setIsSaving(id)
    try {
      await adminNoteApi.update(id, { content })
    } catch (error) {
      console.error('메모 수정 실패:', error)
    } finally {
      setIsSaving(null)
    }
  }

  // 메모 완료 토글
  const toggleComplete = async (id: number) => {
    const note = notes.find(n => n.id === id)
    if (!note) return
    
    const newCompleted = !note.isCompleted
    setNotes(notes.map(n =>
      n.id === id ? { ...n, isCompleted: newCompleted } : n
    ))
    
    try {
      await adminNoteApi.update(id, { isCompleted: newCompleted })
    } catch (error) {
      console.error('메모 완료 상태 변경 실패:', error)
      // 롤백
      setNotes(notes.map(n =>
        n.id === id ? { ...n, isCompleted: !newCompleted } : n
      ))
    }
  }

  // 메모 색상 변경
  const changeColor = async (id: number) => {
    const note = notes.find(n => n.id === id)
    if (!note) return
    
    const currentIndex = NOTE_COLORS.indexOf(note.color)
    const nextIndex = (currentIndex + 1) % NOTE_COLORS.length
    const newColor = NOTE_COLORS[nextIndex]
    
    setNotes(notes.map(n =>
      n.id === id ? { ...n, color: newColor } : n
    ))
    
    try {
      await adminNoteApi.update(id, { color: newColor })
    } catch (error) {
      console.error('메모 색상 변경 실패:', error)
    }
  }

  // 정렬된 메모 (미완료 먼저, 완료는 뒤로)
  const sortedNotes = [...notes].sort((a, b) => {
    if (a.isCompleted === b.isCompleted) {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    }
    return a.isCompleted ? 1 : -1
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('notes.title')}</h1>
          <p className="text-gray-600 mt-1">{t('notes.description')}</p>
        </div>
        <button
          onClick={addNote}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 shadow-sm"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('notes.add')}
        </button>
      </div>

      {/* Notes Grid */}
      {notes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <StickyNote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('notes.empty')}</h3>
          <p className="text-gray-500 mb-4">
            {t('notes.emptyGuide')}
          </p>
          <button
            onClick={addNote}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('notes.createFirst')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {sortedNotes.map((note) => (
            <div
              key={note.id}
              className={clsx(
                "rounded-lg shadow-md p-4 min-h-[200px] flex flex-col relative group transition-all hover:shadow-lg",
                note.color,
                note.isCompleted && "opacity-60 scale-[0.98]"
              )}
            >
              {/* 헤더 - 체크박스 & 색상 변경 & 삭제 */}
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => toggleComplete(note.id)}
                  className={clsx(
                    "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors",
                    note.isCompleted 
                      ? "bg-green-500 border-green-500 text-white" 
                      : "border-gray-400 hover:border-green-500"
                  )}
                  title={note.isCompleted ? t('notes.undoComplete') : t('notes.markComplete')}
                >
                  {note.isCompleted && <Check className="w-4 h-4" />}
                </button>
                <div className="flex items-center gap-1">
                  {isSaving === note.id && (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                  )}
                  <button
                    onClick={() => changeColor(note.id)}
                    className="text-xs text-gray-600 hover:text-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t('notes.changeColor')}
                  >
                    🎨
                  </button>
                  <button
                    onClick={() => deleteNote(note.id)}
                    className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t('common.delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* 메모 내용 - 직접 편집 */}
              <textarea
                value={note.content || ''}
                onChange={(e) => updateNote(note.id, e.target.value)}
                placeholder={t('notes.placeholder')}
                className={clsx(
                  "flex-1 w-full bg-transparent border-none resize-none focus:outline-none placeholder-gray-500",
                  note.isCompleted ? "text-gray-500 line-through" : "text-gray-800"
                )}
                style={{ minHeight: '140px' }}
              />

              {/* 날짜 & 완료 표시 */}
              <div className="flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-black/10">
                <span>
                  {new Date(note.createdAt).toLocaleDateString('ko-KR', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                {note.isCompleted && (
                  <span className="text-green-600 font-medium">✓ {t('notes.completed')}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 메모 개수 표시 */}
      {notes.length > 0 && (
        <div className="text-center text-sm text-gray-500">
          {t('notes.total', { count: notes.length })}
        </div>
      )}
    </div>
  )
}
